import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import type {
  DropDownOption,
  DropDownProps,
  DropDownStateSnapshot,
} from "./types";
import { useIsPc } from "@/src/hooks/useIsPc";

const DEFAULT_PLACEHOLDER = "All";
const DEFAULT_APPLY_TEXT = "Apply Filters";
const DEFAULT_EMPTY_TEXT = "No data";
const DEFAULT_TRIGGER_ARIA_LABEL = "Filters";
const DEFAULT_SELECT_ALL_VALUE = "all";

type NormalizeOptions = {
  allowEmpty?: boolean;
};

const uniqueValues = (values: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    if (!seen.has(value)) {
      seen.add(value);
      result.push(value);
    }
  });
  return result;
};

const arraysShallowEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((value, index) => value === b[index]);
};

export interface UseDropDownResult {
  containerRef: MutableRefObject<HTMLDivElement | null>;
  open: boolean;
  snapshot: DropDownStateSnapshot;
  triggerParts: string[];
  sortedOptions: DropDownOption[];
  toggleOpen: () => void;
  closeDropdown: () => void;
  toggleOption: (option: DropDownOption) => void;
  applySelection: (nextValue?: string[]) => void;
  isSelected: (value: string) => boolean;
  applyButtonText: string;
  emptyText: string;
  triggerAriaLabel: string;
  placeholder: string;
  selectAllOption: DropDownOption | null;
  hasSelection: boolean;
  getSelectedIconName: (option: DropDownOption) => string | undefined;
}

export const useDropDown = (props: DropDownProps): UseDropDownResult => {
  const {
    options: rawOptions,
    value,
    onChange,
    applyOnSelect = false,
    maxVisibleTags = 3,
    enableSelectAll = false,
    allOptionValue,
    onOpenChange,
    placeholder = DEFAULT_PLACEHOLDER,
    applyButtonText = DEFAULT_APPLY_TEXT,
    emptyText = DEFAULT_EMPTY_TEXT,
    triggerAriaLabel = DEFAULT_TRIGGER_ARIA_LABEL,
  } = props;

  const selectAllValue = enableSelectAll
    ? (allOptionValue ?? DEFAULT_SELECT_ALL_VALUE)
    : undefined;

  const selectAllOption = useMemo<DropDownOption | null>(() => {
    if (!selectAllValue) {
      return null;
    }

    const existing = rawOptions.find(
      (option) => option.value === selectAllValue,
    );
    if (existing) {
      return existing;
    }

    return {
      value: selectAllValue,
      label: placeholder,
      meta: { pinned: true },
    };
  }, [placeholder, rawOptions, selectAllValue]);

  const options = useMemo(() => {
    if (!selectAllValue) {
      return rawOptions;
    }

    return rawOptions.filter((option) => option.value !== selectAllValue);
  }, [rawOptions, selectAllValue]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const applyQueueRef = useRef<string[] | null>(null);
  const draftRef = useRef<string[]>([]);
  // 记录打开时的初始值，用于决定排序方式（在整个打开期间保持不变）
  const initialValueOnOpenRef = useRef<string[]>([]);

  const optionValueSet = useMemo(() => {
    const set = new Set<string>();
    options.forEach((option) => {
      set.add(option.value);
    });
    return set;
  }, [options]);

  const selectableOptionValues = useMemo(() => {
    if (!selectAllValue) {
      return options
        .filter((option) => !option.disabled)
        .map((option) => option.value);
    }

    return options
      .filter((option) => !option.disabled && option.value !== selectAllValue)
      .map((option) => option.value);
  }, [options, selectAllValue]);

  const normalizeSelection = useCallback(
    (source: string[], options?: NormalizeOptions) => {
      const { allowEmpty = false } = options ?? {};
      const deduplicated = uniqueValues(source).filter(
        (valueItem) =>
          optionValueSet.has(valueItem) && valueItem !== selectAllValue,
      );

      if (
        !allowEmpty &&
        deduplicated.length === 0 &&
        selectableOptionValues.length > 0
      ) {
        return [...selectableOptionValues];
      }

      return deduplicated;
    },
    [optionValueSet, selectableOptionValues, selectAllValue],
  );

  const normalizedValue = useMemo(
    () => normalizeSelection(value, { allowEmpty: !enableSelectAll }),
    [enableSelectAll, normalizeSelection, value],
  );

  const [open, setOpenState] = useState(false);
  const [draft, setDraft] = useState<string[]>(normalizedValue);
  const [isPc] = useIsPc();

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    const next = normalizeSelection(value, { allowEmpty: true });
    if (!arraysShallowEqual(draftRef.current, next)) {
      setDraft(next);
      draftRef.current = next;
    }
  }, [value, normalizeSelection]);

  const updateOpen = useCallback(
    (next: boolean) => {
      setOpenState(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  const closeDropdown = useCallback(
    (nextValue?: string[]) => {
      const fallback =
        nextValue ?? normalizeSelection(value, { allowEmpty: true });
      setDraft(fallback);
      draftRef.current = fallback;
      // 清除打开时的初始值记录
      initialValueOnOpenRef.current = [];
      updateOpen(false);
    },
    [normalizeSelection, updateOpen, value],
  );

  const openDropdown = useCallback(() => {
    const next = normalizeSelection(value, { allowEmpty: true });
    setDraft(next);
    draftRef.current = next;
    // 记录打开时的初始值，用于决定排序方式
    initialValueOnOpenRef.current = [...next];
    updateOpen(true);
  }, [normalizeSelection, updateOpen, value]);

  const toggleOpen = useCallback(() => {
    if (open) {
      closeDropdown();
    } else {
      openDropdown();
    }
  }, [closeDropdown, open, openDropdown]);

  const optionMap = useMemo(() => {
    const map = new Map<string, DropDownOption>();
    options.forEach((option) => {
      map.set(option.value, option);
    });
    return map;
  }, [options]);

  const computeNextDraft = useCallback(
    (current: string[], optionValue: string) => {
      if (selectAllValue && optionValue === selectAllValue) {
        const allSelected =
          selectableOptionValues.length > 0 &&
          selectableOptionValues.every((candidate) =>
            current.includes(candidate),
          );
        return normalizeSelection(allSelected ? [] : selectableOptionValues, {
          allowEmpty: true,
        });
      }

      const exists = current.includes(optionValue);
      const next = exists
        ? current.filter((item) => item !== optionValue)
        : [...current, optionValue];

      if (
        selectAllValue &&
        selectableOptionValues.length > 0 &&
        selectableOptionValues.every((candidate) => next.includes(candidate))
      ) {
        return normalizeSelection(selectableOptionValues, { allowEmpty: true });
      }

      return normalizeSelection(next, { allowEmpty: true });
    },
    [normalizeSelection, selectableOptionValues, selectAllValue],
  );

  const applySelection = useCallback(
    (nextValue?: string[]) => {
      const target = normalizeSelection(nextValue ?? draftRef.current, {
        allowEmpty: true,
      });

      const snapshot: DropDownStateSnapshot = {
        open: false,
        value: target,
        draft: target,
      };

      onChange(target, snapshot);
      draftRef.current = target;
      closeDropdown(target);
    },
    [closeDropdown, normalizeSelection, onChange],
  );

  const toggleOption = useCallback(
    (option: DropDownOption) => {
      if (option.disabled) {
        return;
      }

      setDraft((prev) => {
        const next = computeNextDraft(prev, option.value);
        draftRef.current = next;
        if (applyOnSelect) {
          applyQueueRef.current = next;
        }
        return next;
      });
    },
    [applyOnSelect, computeNextDraft],
  );

  useEffect(() => {
    if (!applyOnSelect) {
      applyQueueRef.current = null;
      return;
    }

    if (applyQueueRef.current) {
      const pending = applyQueueRef.current;
      applyQueueRef.current = null;
      applySelection(pending);
    }
  }, [applyOnSelect, applySelection, draft]);

  useEffect(() => {
    // 仅在 PC 端启用“点击外部关闭”逻辑，避免移动端 Drawer 内容被视为组件外部导致误关闭
    if (!open || !isPc) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (
        target &&
        containerRef.current &&
        !containerRef.current.contains(target)
      ) {
        closeDropdown();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeDropdown();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeDropdown, isPc, open]);

  const draftSet = useMemo(() => new Set(draft), [draft]);
  const hasSelection = draftSet.size > 0;

  // 判断是否应该按初始状态排序（打开时 draft 与已应用的值相同）
  // 使用打开时记录的初始值，确保在整个打开期间排序方式保持不变
  const shouldSortByInitialState = useMemo(() => {
    if (!open || initialValueOnOpenRef.current.length === 0) {
      return false;
    }
    return arraysShallowEqual(initialValueOnOpenRef.current, normalizedValue);
  }, [open, normalizedValue]);

  const sortedOptions = useMemo(() => {
    const pinnedValues = new Set<string>();
    const pinned: DropDownOption[] = [];
    const others: DropDownOption[] = [];
    const selectedOthers: DropDownOption[] = [];

    // 使用打开时的初始值来判断选中状态（用于排序）
    const initialValueSet = new Set(initialValueOnOpenRef.current);

    options.forEach((option) => {
      if (option.meta?.pinned) {
        pinnedValues.add(option.value);
        pinned.push(option);
      } else if (shouldSortByInitialState) {
        // 初始打开时，选中项排在未选中项前面（基于打开时的初始值）
        if (initialValueSet.has(option.value)) {
          selectedOthers.push(option);
        } else {
          others.push(option);
        }
      } else {
        // 选择过程中，保持原始顺序
        others.push(option);
      }
    });

    // 初始状态：pinned → 选中项 → 未选中项
    // 编辑状态：pinned → 所有项（原始顺序）
    return shouldSortByInitialState
      ? [...pinned, ...selectedOthers, ...others]
      : [...pinned, ...others];
  }, [shouldSortByInitialState, options]);

  const isAllValueApplied = useMemo(
    () =>
      enableSelectAll &&
      selectAllValue &&
      selectableOptionValues.length > 0 &&
      selectableOptionValues.every((candidate) =>
        normalizedValue.includes(candidate),
      ),
    [enableSelectAll, normalizedValue, selectAllValue, selectableOptionValues],
  );

  const displayValues = useMemo(() => {
    if (isAllValueApplied) {
      return [placeholder];
    }
    return normalizedValue;
  }, [isAllValueApplied, normalizedValue, placeholder]);

  const triggerParts = useMemo(() => {
    const labels = displayValues.map(
      (item) => optionMap.get(item)?.label ?? item,
    );
    const hasLabels = labels.length > 0;

    const base = hasLabels ? labels : [placeholder];
    const limited = base.slice(0, maxVisibleTags);
    const overflow = hasLabels ? base.length - limited.length : 0;

    if (overflow > 0) {
      limited.push(`+${overflow}`);
    }

    return limited;
  }, [displayValues, maxVisibleTags, optionMap, placeholder]);

  const allSelectionStatus = useMemo(() => {
    if (!selectAllValue || selectableOptionValues.length === 0) {
      return {
        allSelected: false,
        anySelected: false,
      };
    }

    let count = 0;
    selectableOptionValues.forEach((valueItem) => {
      if (draftSet.has(valueItem)) {
        count += 1;
      }
    });

    return {
      allSelected: count === selectableOptionValues.length && count > 0,
      anySelected: count > 0,
    };
  }, [draftSet, selectableOptionValues, selectAllValue]);

  const isSelected = useCallback(
    (optionValue: string) => {
      if (selectAllValue && optionValue === selectAllValue) {
        return allSelectionStatus.anySelected;
      }
      return draftSet.has(optionValue);
    },
    [allSelectionStatus.anySelected, draftSet, selectAllValue],
  );

  const getSelectedIconName = useCallback(
    (option: DropDownOption) => {
      if (option.meta?.selectedIconName) {
        return option.meta.selectedIconName;
      }
      if (selectAllValue && option.value === selectAllValue) {
        if (!allSelectionStatus.anySelected) {
          return undefined;
        }
        if (allSelectionStatus.allSelected) {
          return "check";
        }
        return "minus";
      }
      return "check";
    },
    [
      allSelectionStatus.allSelected,
      allSelectionStatus.anySelected,
      selectAllValue,
    ],
  );

  const snapshot = useMemo<DropDownStateSnapshot>(
    () => ({
      open,
      value: normalizedValue,
      draft,
    }),
    [draft, normalizedValue, open],
  );

  return {
    containerRef,
    open,
    snapshot,
    triggerParts,
    sortedOptions,
    toggleOpen,
    closeDropdown,
    toggleOption,
    applySelection,
    isSelected,
    applyButtonText,
    emptyText,
    triggerAriaLabel,
    placeholder,
    selectAllOption,
    hasSelection,
    getSelectedIconName,
  };
};
